import { Bell, BellOff, Check, FileText } from 'lucide-react';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useDeregisterPushToken,
  useRegisterPushToken,
  useWebPushPublicKey,
} from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Alert } from '@/components/ui/alert';
import { useToast } from '@/components/ui/use-toast';
import { usePushSubscription } from '@/features/push/usePushSubscription';

export function NotificationsScreen() {
  const { notifications, loading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const unread = notifications.filter((n) => !n.readAt);

  return (
    <Page>
      <PageHeader
        title="Notifications"
        description="Email opens, reminders, payments and system alerts."
        actions={
          unread.length > 0 ? (
            <Button variant="outline" onClick={() => markAll.markAllRead()}>
              <Check className="size-4" /> Mark all read
            </Button>
          ) : null
        }
      />

      <PushSettings />

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, idx) => (
            <Skeleton key={idx} className="h-16 rounded-lg" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Bell className="size-7" />}
              title="No notifications"
              description="When something happens with your invoices, you'll see it here."
            />
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {notifications.map((n) => (
            <li
              key={n.id}
              className={`flex items-start gap-3 rounded-lg border p-4 transition-colors ${
                n.readAt ? 'border-line bg-card' : 'border-accent/40 bg-accent-soft/40'
              }`}
            >
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                <FileText className="size-4" />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-sm font-medium text-foreground">{n.title}</h4>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(n.createdAt).toLocaleString()}
                  </span>
                </div>
                {n.body ? <p className="text-sm text-muted-foreground">{n.body}</p> : null}
                <div className="mt-1 flex items-center gap-2">
                  <Badge variant="default">{n.kind.replace(/[._]/g, ' ')}</Badge>
                  {!n.readAt ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => markRead.markRead(n.id)}
                    >
                      Mark read
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Page>
  );
}

function PushSettings() {
  const push = usePushSubscription();
  const { data: vapid } = useWebPushPublicKey();
  const { register } = useRegisterPushToken();
  const { deregister } = useDeregisterPushToken();
  const { toast } = useToast();

  if (!push.supported) return null;

  const enabled = push.permission === 'granted' && push.subscription !== null;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">Browser notifications</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">
              Get a desktop ping when an invoice is opened, paid, or goes overdue —
              even when this tab is in the background.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Permission:{' '}
              <span className="font-mono uppercase tracking-[0.16em]">
                {push.permission}
              </span>
            </p>
          </div>
          <Button
            variant={enabled ? 'outline' : 'primary'}
            disabled={push.busy}
            onClick={async () => {
              if (enabled) {
                const token = push.subscription?.toJSON();
                const ok = await push.unsubscribe();
                if (ok && token) {
                  const wire = JSON.stringify(token);
                  await deregister(wire);
                }
                if (ok) toast({ title: 'Notifications disabled', variant: 'success' });
                return;
              }
              const sub = await push.subscribe(vapid?.publicKey ?? undefined);
              if (sub) {
                const wire = JSON.stringify(sub.toJSON());
                await register({ token: wire, platform: 'web' });
                toast({
                  title: 'Notifications enabled',
                  description: "We'll alert you when something happens with your invoices.",
                  variant: 'success',
                });
              } else if (push.error) {
                toast({
                  title: "Couldn't enable notifications",
                  description: push.error,
                  variant: 'danger',
                });
              }
            }}
          >
            {enabled ? (
              <>
                <BellOff className="size-4" /> Disable
              </>
            ) : (
              <>
                <Bell className="size-4" /> Enable
              </>
            )}
          </Button>
        </div>
        {push.error && !push.busy ? (
          <Alert>{push.error}</Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
